type MaybeNumber = number | string | null | undefined;

export type ArchiveSortableRow = {
  id?: string | number | null;
  index?: MaybeNumber;
  archive_index?: MaybeNumber;
  archived_at?: string | null;
};

function toNumberOrNull(value: MaybeNumber): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function toTimeMsOrNull(value?: string | null): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

/**
 * Sort archived components so the newest archived appears first.
 *
 * Priority:
 * 1) `archive_index` (highest first) — enables stable archive drag+drop ordering
 * 2) `archived_at` (most recent first) — ensures newly archived bubbles to top
 * 3) `index` (highest first) — backwards-compatible fallback for pre-migration data
 * 4) `id` (desc) — stable tie-breaker
 */
export function sortArchivedComponents<T extends ArchiveSortableRow>(rows: T[]): T[] {
  const copy = Array.isArray(rows) ? [...rows] : [];
  copy.sort((a, b) => {
    const ai = toNumberOrNull(a.archive_index);
    const bi = toNumberOrNull(b.archive_index);
    if (ai !== null || bi !== null) {
      const aa = ai ?? -1;
      const bb = bi ?? -1;
      if (bb !== aa) return bb - aa;
    }

    const at = toTimeMsOrNull(a.archived_at);
    const bt = toTimeMsOrNull(b.archived_at);
    if (at !== null || bt !== null) {
      const aa = at ?? 0;
      const bb = bt ?? 0;
      if (bb !== aa) return bb - aa;
    }

    const ax = toNumberOrNull(a.index) ?? -1;
    const bx = toNumberOrNull(b.index) ?? -1;
    if (bx !== ax) return bx - ax;

    const aid = String(a.id ?? "");
    const bid = String(b.id ?? "");
    return bid.localeCompare(aid);
  });
  return copy;
}

/** Assign descending `archive_index` values to match visual list order (top = highest). */
export function applyArchiveIndices<T extends ArchiveSortableRow>(rows: T[]): T[] {
  return rows.map((item, idx, arr) => ({
    ...item,
    archive_index: arr.length - 1 - idx,
  }));
}

/** Reorder the visible slice of an archive list and keep hidden tail items fixed. */
export function reorderArchiveVisibleSlice<T extends ArchiveSortableRow>(
  fullList: T[],
  sourceIndex: number,
  destinationIndex: number,
  visibleCount: number
): T[] {
  const full = Array.isArray(fullList) ? [...fullList] : [];
  const count = Math.min(visibleCount, full.length);
  const visible = full.slice(0, count);
  const hidden = full.slice(count);
  const reordered = [...visible];
  const [removed] = reordered.splice(sourceIndex, 1);
  reordered.splice(destinationIndex, 0, removed);
  return applyArchiveIndices([...reordered, ...hidden]);
}

